/* eslint-disable no-unsafe-optional-chaining */
import {env} from 'next-runtime-env';
import {
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useRouter} from 'next/router';
import useCommon from '@utils/hooks/common';
import styles from './styles';
import {CHIP_TRANSITION_DURATION, PLAYER_LAYOUT} from '@constant/game';
import classNames from 'classnames';
import {PositionContext} from '@utils/context';
import {AbsolutePosition, BestCombination, PlayerComponent, PokerComponent} from '@interface';
import {TABLET_BREAKPOINT} from '@constant/system';
import {debounce, getCumulativeOffset, getSafe} from '@utils/index';
import {GameLogicInterface, GameRoomInterface} from 'poker-interfaces';
import React from 'react';
import PotArea from '@components/game-room/PotArea';
import GameInfoArea from '@components/game-room/GameRoom/GameInfoArea';
import {useWhyDidYouUpdate} from 'ahooks';
import {JoinedRoomProps} from 'poker-interfaces/game-room/game-room-interface';

import Player from '@components/game-room/Player';
import EmptySeat from '@components/game-room/EmptySeat/canvas';
import {cloneDeep} from 'lodash-es';
import CanvasTable from '../CanvasTable';
import {useDebugView} from '@utils/hooks/debug';
import dayjs from 'dayjs';

// import Poker from '@components/Poker';
// import classnames from 'classnames'

const NEXT_PUBLIC_URL = env('NEXT_PUBLIC_URL');

interface RegularTableInterface {
  tableIndex: number;
  pot: number;
  renderOutCards: () => Array<JSX.Element>;
  renderCommunityHands: () => Array<JSX.Element>;
  playerSequence: Array<PlayerComponent>;
  playerAbsolutePosition?: Array<AbsolutePosition>;
  dealerBoxPosition?: number;
  gameMode?: GameLogicInterface.GameType;
  roomType?: GameRoomInterface.RoomType;

  isAllInMode?: boolean;
  myHands?: Array<PokerComponent>;
  myBestCombination?: BestCombination;
  isTimerHidden?: boolean;
  gameResultOnScreen?: GameLogicInterface.Result;
  actionTime?: number;
  playerInAction?: number;
  buyInCallback?: (position: number) => void;
  isSeated?: boolean;
  isCurrentTableInView: boolean;

  setActionTimerStatus?: React.Dispatch<
    React.SetStateAction<{order: number; expiredAt: dayjs.Dayjs; duration: number}>
  >;
  actionTimerStatusOrder?: number;
  isMyselfSeated?: boolean;
}

const mockPromise = async value => {
  return value;
};

const RegularTable = React.forwardRef((props: RegularTableInterface, ref): JSX.Element => {
  const {classes, theme, game, t, user, system} = useCommon({
    withStyles: styles,
    withTranslation: ['table_area', 'best_combination', 'common'],
    observers: ['socket', 'game', 'user', 'system'],
  });

  const {scaleUpValue, updatePlayerIconCenterPositions} = useContext(PositionContext);

  // useWhyDidYouUpdate('RegularTable', {...props});

  const isDebugView = useDebugView();

  const {isGameOverlayShown} = system;
  const jsonCache = useRef<{key: string; value: JSON} | {}>({});
  const tableAreaRef = useRef<HTMLDivElement>();
  const isDesktop = theme?.currentStyleFormat?.id === 'desktop';

  const {
    tableIndex,
    pot,
    renderOutCards,
    renderCommunityHands,
    playerSequence,
    playerAbsolutePosition,
    dealerBoxPosition,
    gameMode,
    roomType,
    isCurrentTableInView,

    isAllInMode,
    myHands,
    myBestCombination,
    isTimerHidden,
    playerInAction,
    actionTime,
    gameResultOnScreen,
    buyInCallback,
    isSeated,
    isMyselfSeated,

    setActionTimerStatus,
    actionTimerStatusOrder,
  } = props;

  const [loadingPosition, setLoadingPosition] = useState<number>(null);

  const player0Ref = useRef<PlayerComponent>();
  const player1Ref = useRef<PlayerComponent>();
  const player2Ref = useRef<PlayerComponent>();
  const player3Ref = useRef<PlayerComponent>();
  const player4Ref = useRef<PlayerComponent>();
  const player5Ref = useRef<PlayerComponent>();

  const seatsRef = useRef<HTMLElement[]>([]);

  const playersRefArray = useMemo(
    () => [player0Ref, player1Ref, player2Ref, player3Ref, player4Ref, player5Ref],
    [player0Ref, player1Ref, player2Ref, player3Ref, player4Ref, player5Ref]
  );

  useEffect(() => {
    setTimeout(() => {
      // Let DOM update before re-calculation
      getPlayersPositions();
    }, 500);

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const handleResizeEnd = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        getPlayersPositions();
      }, 500); // adjust delay as needed
    };

    window.addEventListener('resize', handleResizeEnd);
    return () => {
      window.removeEventListener('resize', handleResizeEnd);
    };
  }, [isDesktop, scaleUpValue, isCurrentTableInView]);

  useImperativeHandle(
    ref,
    () => ({
      collectAllChipsToPot: () => {
        return new Promise(resolve => {
          playersRefArray.forEach(player => {
            if (player?.current) {
              player?.current.moveChipToPot();
            }
          });
          setTimeout(() => {
            resolve(null);
          }, CHIP_TRANSITION_DURATION);
        });
      },
      resetPlayerTimer: (playerOrder: number, expiredAt: dayjs.Dayjs, duration: number) => {
        // console.log('resetPlayerTimer', playerOrder, playersRefArray[playerOrder]?.current);
        const uiIndex = playerSequence.findIndex(player => player && player.order === playerOrder);
        // original: if (!actionTimerStatus) {
        // if (actionTimerStatus?.order !== playerOrder) {
        setActionTimerStatus({
          expiredAt,
          duration,
          order: playerOrder,
        });
        // }
        playersRefArray[uiIndex]?.current?.resetTimer({expiredAt, duration});
      },
      unhidePlayerTimer: () => {
        playersRefArray.forEach(player => {
          if (player?.current) {
            player?.current?.unhideTimer();
          }
        });
      },
      hidePlayerTimer: () => {
        playersRefArray.forEach(player => {
          if (player?.current) {
            player?.current.hideTimer();
          }
        });
      },
      distributeCards: () => {
        playersRefArray.forEach(player => {
          if (player?.current) {
            player?.current.distributeCards();
          }
        });
      },
    }),
    [
      // movableObjRef,
      playersRefArray,
      playerSequence,
      actionTimerStatusOrder,
      setActionTimerStatus,
    ]
  );

  // if log in useEffect, it will show initial state
  // console.log('targetButtonPos', targetButtonPos);

  const router = useRouter();

  const getTableImg = () => {
    if (isDesktop) {
      if (gameMode === 'OMAHA') {
        return '/images-mobile/game-room/tables/horizontal/web3/blue-table-regular.webp';
      }
      if (gameMode === 'SHORT') {
        return '/images-mobile/game-room/tables/horizontal/web3/red-table-regular.webp';
      }
      if (gameMode === 'REGULAR' && (roomType === 'PRIVATE' || router.query.isPrivate === 'true')) {
        return '/images-mobile/game-room/tables/horizontal/web3/green-table-regular.webp';
      }
      if (isDebugView)
        return '/images-mobile/game-room/tables/horizontal/web3/green-table-regular.webp';
      return '/images-mobile/game-room/tables/horizontal/web3/green-table-regular.webp';
    }
    if (!isDesktop) {
      if (gameMode === 'OMAHA') {
        return '/images-mobile/game-room/tables/vertical/blue-table.webp';
      }
      if (gameMode === 'SHORT') {
        return '/images-mobile/game-room/tables/vertical/red-table.webp';
      }
      if (gameMode === 'REGULAR' && (roomType === 'PRIVATE' || router.query.isPrivate === 'true')) {
        return '/images-mobile/game-room/tables/vertical/green-table.webp';
      }
      if (isDebugView) return '/images-mobile/game-room/tables/vertical/green-table.webp';
      return '/images-mobile/game-room/tables/vertical/green-table.webp';
    }
  };

  const getCache = (key: string): JSON => {
    return jsonCache.current[key];
  };

  const setCache = (key: string, value: JSON): void => {
    jsonCache.current[key] = value;
  };

  const displayDealerButton = (): JSX.Element => (
    <article
      className={classNames(classes.dealerButton, {
        position_0: dealerBoxPosition === 0,
        position_1: dealerBoxPosition === 1,
        position_2: dealerBoxPosition === 2,
        position_3: dealerBoxPosition === 3,
        position_4: dealerBoxPosition === 4,
        position_5: dealerBoxPosition === 5,
        hidden: typeof dealerBoxPosition === 'undefined',
      })}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src="/images-mobile/game-room/Player/dealer-button-label.webp"
        alt="dealer-label"
        className={classes.dealerLabel}
      />
    </article>
  );

  const getSeatCenterPosition = (index): AbsolutePosition => {
    if (!seatsRef?.current[index]) return null;
    let position;

    if (playersRefArray?.[index]?.current) {
      position = playersRefArray?.[index]?.current?.containerElement?.getBoundingClientRect();
    } else {
      position = seatsRef?.current[index]?.getBoundingClientRect();
    }

    const {top, left, width, height} = position;
    const centerXOfPlayerIcon = left + width / 2;
    const centerYOfPlayerIcon = top + height / 2;
    return {
      x: centerXOfPlayerIcon,
      y: centerYOfPlayerIcon,
      xCenter: centerXOfPlayerIcon,
      yCenter: centerYOfPlayerIcon,
      top,
      left,
    };
  };

  const getPlayersPositions = (): void => {
    const refs = seatsRef;
    // const absolutePositionArray: AbsolutePosition[] = Array(6).fill(null);
    const playerIconCenterPositionArray = Array(6).fill(null);

    let shouldUpdateState = false;
    if (refs && refs?.current.length > 0) {
      refs.current.forEach((seatRef, index) => {
        // console.log('playerRef', seatRef);
        if (seatRef) {
          // console.log(' !!!! updating absolutePositionArray !!!!');
          // absolutePositionArray[index] = getSeatPosition(index);
          playerIconCenterPositionArray[index] = getSeatCenterPosition(index);
          shouldUpdateState = true;
        }
      });

      if (shouldUpdateState) {
        // console.log('playerIconCenterPositionArray', playerIconCenterPositionArray);
        // will update to all null for some reasons during render cycle
        updatePlayerIconCenterPositions(playerIconCenterPositionArray);
      }
    }
  };

  const tableInfo: JoinedRoomProps =
    tableIndex < user?.joinedRooms?.length ? user?.joinedRooms[tableIndex] : null;

  const isInAction = useCallback(
    (playerOrder: number) => {
      if (gameResultOnScreen) return false;
      if (playerOrder === playerInAction) return true;
      return false;
    },
    [gameResultOnScreen, playerInAction]
  );

  const isPlayerTheWinner = useCallback(
    (playerOrder: number): boolean => {
      // TODO: Side pot support
      if (!gameResultOnScreen) return false;
      const currentPlayer = playerSequence?.find(player => player?.order === playerOrder);
      return gameResultOnScreen.potWinnerIds.includes(currentPlayer?.id);
    },
    [gameResultOnScreen, playerSequence]
  );

  const getPlayersHands = useCallback(
    (playerOrder: number): Array<PokerComponent> => {
      return getSafe(() => playerSequence[playerOrder].hand, null);
    },
    [playerSequence]
  );

  const onBuyIn = useCallback(
    (sequenceOrder: number) => {
      setLoadingPosition(sequenceOrder);
      setTimeout(() => {
        setLoadingPosition(null);
      }, 2000);
      console.log('buy in at position', playerSequence[sequenceOrder]);
      if (buyInCallback) {
        buyInCallback(playerSequence[sequenceOrder].originalIndex);
      }
    },
    [buyInCallback, playerSequence]
  );

  const assignRef = useCallback(index => {
    return playersRefArray[index];
  }, []);

  const renderPlayer = (sequenceOrder: number, layout: PLAYER_LAYOUT) => {
    if (
      !playerSequence[sequenceOrder] ||
      typeof playerSequence[sequenceOrder]?.id === 'undefined'
    ) {
      return (
        <EmptySeat
          isAllIn={isAllInMode}
          position={sequenceOrder}
          isSeated={isSeated}
          onClick={() => onBuyIn(sequenceOrder)}
          loadingPosition={loadingPosition}
          isMyselfSeated={isMyselfSeated}
        />
      );
    }

    const totalPlayer = playerSequence?.filter(player => player?.id)?.length;

    return (
      <Player
        myHands={myHands}
        myBestCombination={myBestCombination}
        isTimerHidden={isTimerHidden}
        actionTime={actionTime}
        isResultShowing={!!gameResultOnScreen}
        // only get players hand when there game result is reached
        hand={getPlayersHands(playerSequence[sequenceOrder].order)}
        isWinner={isPlayerTheWinner(playerSequence[sequenceOrder].order)}
        isInAction={isInAction(playerSequence[sequenceOrder].order)}
        layout={layout}
        icon={playerSequence[sequenceOrder].icon}
        ref={assignRef(sequenceOrder)}
        gameMode={gameMode}
        isAllInMode={isAllInMode}
        isAllIn={playerSequence[sequenceOrder].isAllIn}
        // roomCode={roomCode}
        outCards={null}
        winPercentage={null}
        sequenceOrder={sequenceOrder}
        {...playerSequence[sequenceOrder]}
        availableActions={null}
        noOfPlayers={totalPlayer}
        tableElement={tableAreaRef.current}
        isCurrentTableInView={isCurrentTableInView}
        roomCode={tableInfo?.roomCode}
      />
    );
  };

  return (
    <div ref={tableAreaRef} className={classes.tableArea}>
      {displayDealerButton()}
      <CanvasTable imagePath={getTableImg()} className={classes.neonTable} />
      <GameInfoArea sb={tableInfo?.smallBlind} bb={tableInfo?.bigBlind} gameMode={gameMode} />
      <PotArea pot={pot} />

      <section className={classes.cardsOnTable}>{renderCommunityHands()}</section>

      <article
        ref={el => {
          seatsRef.current[3] = el;
        }}
        className={classes.topMiddle}
      >
        {renderPlayer(3, PLAYER_LAYOUT.PLAYER_TOP_ACTION_BOTTOM)}
      </article>
      <article
        ref={el => {
          seatsRef.current[2] = el;
        }}
        className={classes.middleLeft}
      >
        {renderPlayer(2, PLAYER_LAYOUT.PLAYER_LEFT_ACTION_RIGHT)}
      </article>
      <article
        ref={el => {
          seatsRef.current[1] = el;
        }}
        className={classes.bottomLeft}
      >
        {renderPlayer(1, PLAYER_LAYOUT.PLAYER_LEFT_ACTION_RIGHT)}
      </article>
      <article
        ref={el => {
          seatsRef.current[0] = el;
        }}
        className={classes.bottomMiddle}
      >
        {renderPlayer(
          0,
          isDesktop
            ? PLAYER_LAYOUT.PLAYER_BOTTOM_ACTION_TOP
            : PLAYER_LAYOUT.PLAYER_LEFT_ACTION_RIGHT_TOP
        )}
      </article>
      <article
        ref={el => {
          seatsRef.current[4] = el;
        }}
        className={classes.middleRight}
      >
        {renderPlayer(4, PLAYER_LAYOUT.PLAYER_RIGHT_ACTION_LEFT)}
      </article>
      <article
        ref={el => {
          seatsRef.current[5] = el;
        }}
        className={classes.bottomRight}
      >
        {renderPlayer(5, PLAYER_LAYOUT.PLAYER_RIGHT_ACTION_LEFT)}
      </article>
    </div>
  );
});

RegularTable.displayName = 'RegularTable';

export default RegularTable;
